import React, { useEffect, useState, forwardRef } from "react";
import { Input, Select, Button, Form, Card, Upload, message, Spin } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import FeatherIcon from "feather-icons-react";
import { X, PlusCircle, Download } from "react-feather";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";
import { secureReadExcelFile } from "../../utils/secureExcelUtils";
import { useSelector } from "react-redux";
const { Option } = Select;
import {
  createSalesDumpFile,
  getSalesDumpFileById,
  updateSalesDumpFile,
} from "../../core/api/report";
import { Modal } from "react-bootstrap";

const UploadSalesDumpFile = forwardRef(
  ({ isVisible, onClose, onResize, clientId, reportId }, ref) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [form] = Form.useForm();
    const [brands, setBrands] = useState([]);
    const data = useSelector((state) => state.toggle_header);
    const [fileUrl, setFileUrl] = useState(null);
    const [initialValues, setInitialValues] = useState({
      brand_id: null,
      comments: "",
    });
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
      if (!reportId) return;
      setIsUploading(true);
      const fetchReportData = async () => {
        try {
          const response = await getSalesDumpFileById(reportId);
          const reportData = response.data;
          setInitialValues({
            brand_id: reportData.brand,
            comments: reportData.comments,
            date: reportData.upload_date,
          });

          setFileUrl(reportData.signed_url);
          setIsUploading(false);
        } catch (error) {
          console.error("Failed to fetch report details", error);
          message.error("Could not load report details.");
        }
      };

      fetchReportData();
    }, [reportId]);

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    useEffect(() => {
      const fetchBrands = async () => {
        if (!clientId) return;
        try {
          const response = await fetchBrandsBySelectedClients([clientId]);
          const fetchedBrands = response.data;
          setBrands(fetchedBrands);
          if (fetchedBrands.length === 1) {
            setInitialValues((prevValues) => ({
              ...prevValues,
              brand_id: fetchedBrands[0].id,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch brands", err);
        }
      };

      fetchBrands();
    }, [clientId]);

    useEffect(() => {
      if (brands.length === 0 || !isVisible) return;
      form.setFieldsValue({
        brand_id:
          brands.find((b) => b.id === initialValues.brand_id)?.id || null,
        comments: initialValues.comments,
        date: initialValues.date
          ? initialValues.date.split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    }, [brands, initialValues, isVisible, form]);

    const handleSubmit = async () => {
      setIsUploading(true);
      try {
        const values = await form.validateFields();
        const formData = new FormData();
        formData.append("brand", values.brand_id);
        formData.append("comments", values.comments || "");
        formData.append("upload_date", values.date);

        if (values.file?.file) {
          formData.append("file", values.file.file);
        }

        if (reportId) {
          await updateSalesDumpFile(reportId, formData);
          message.success("Report updated successfully.");
        } else {
          if (!values.file?.file) {
            message.error("Please upload an Excel file.");
            setIsUploading(false);
            return;
          }
          try {
            await createSalesDumpFile(formData);
            message.success("File uploaded successfully.");
          } catch (uploadError) {
            if (uploadError.response && uploadError.response.data && uploadError.response.data.error) {
              message.error(uploadError.response.data.error);
              setIsUploading(false);
              return;
            } else {
              throw uploadError; // Re-throw if it's not our expected error format
            }
          }
        }
        setIsUploading(false);

        form.resetFields();
        setFileUrl(null);
        onClose();
      } catch (error) {
        setIsUploading(false);
        console.error("Validation failed:", error);
        message.error("Please fill all required fields correctly.");
      }
    };

    const beforeUpload = (file) => {
      try {
        secureReadExcelFile(file, {
          validateOnly: true,
          maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB limit
          maxRows: 100000, // Limit rows to prevent DoS attacks
        });
        return false; // Still return false to prevent auto-upload
      } catch (error) {
        message.error(error.message || "Invalid Excel file");
        return false;
      }
    };
    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <h5>Upload Sales Dump File</h5>
          <div>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn"
            >
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>
            <button
              className="btn"
              onClick={() => {
                onClose();
                form.resetFields();
                setFileUrl(null);
              }}
            >
              <X />
            </button>
          </div>
        </div>

        <div className="p-3">
          <Modal show={isUploading} centered>
            <Modal.Body className="text-center">
              <Spin size="large" />
              <p className="mt-2">Uploading, please wait...</p>
            </Modal.Body>
          </Modal>
          <Form form={form} layout="vertical">
            <Card title="File Details">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <Form.Item
                  label="Select Brand"
                  name="brand_id"
                  rules={[{ required: true, message: "Please select a brand" }]}
                >
                  <Select
                    disabled={reportId}
                    placeholder="Select Brand"
                    allowClear
                  >
                    {brands.map((brand) => (
                      <Option key={brand.id} value={brand.id}>
                        {brand.brand_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Upload Date"
                  name="date"
                  disabled={reportId}
                  rules={[
                    { required: true, message: "Please select an upload date" },
                  ]}
                  initialValue={new Date().toISOString().split("T")[0]}
                >
                  <Input type="date" />
                </Form.Item>
              </div>
              <Form.Item label="Uploaded File" name={"file"}>
                {fileUrl ? (
                  <Button
                    type="primary"
                    icon={<Download />}
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = fileUrl;
                      link.download = fileUrl.split("/").pop();
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Download Excel File
                  </Button>
                ) : (
                  <Upload.Dragger
                    name="file"
                    accept=".xls,.xlsx"
                    maxCount={1}
                    beforeUpload={beforeUpload}
                    showUploadList={true}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag Excel file to upload
                    </p>
                    <p className="ant-upload-hint">
                      Only .xls or .xlsx files are allowed
                    </p>
                  </Upload.Dragger>
                )}
              </Form.Item>

              <Form.Item label="Comments" name="comments">
                <Input.TextArea placeholder="Comments (optional)" rows={3} />
              </Form.Item>
              <div>
                <Button
                  type="primary"
                  className="btn btn-primary me-2"
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                  icon={<PlusCircle className="me-0 iconsize" />}
                >
                  Save{" "}
                </Button>
                <Button
                  type="primary"
                  className="btn btn-secondary"
                  onClick={() => {
                    onClose();
                    form.resetFields();
                    setFileUrl(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Card>
  
            <div style={{ textAlign: "right", marginTop: "20px" }}></div>
          </Form>
        </div>
      </div>
    );
  }
);

UploadSalesDumpFile.displayName = "UploadSalesDumpFile";

export default UploadSalesDumpFile;
